/**
 * Supplier portal — completely isolated from the agency UI. Lives at
 * `/supplier/*` and uses its own JWT cookie (`supplier_token`). The
 * navbar, branding and even the auth context are dedicated so a
 * super_admin doesn't accidentally bleed into the supplier session.
 */
import { useEffect, useState, useCallback, createContext, useContext, useMemo } from 'react';
import axios from 'axios';
import { Link, NavLink, Navigate, useNavigate, Outlet, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const supplierAxios = axios.create({ baseURL: API, withCredentials: true });

// ── Auth context (portal-scoped) ────────────────────────────────────
const SupplierAuthContext = createContext(null);

export function SupplierAuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = pending, null = anon

  const refresh = useCallback(async () => {
    try {
      const r = await supplierAxios.get('/api/supplier/auth/me');
      setUser(r.data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const r = await supplierAxios.post('/api/supplier/auth/login', { email, password });
    await refresh();
    return r.data;
  };
  const logout = async () => {
    try { await supplierAxios.post('/api/supplier/auth/logout'); } catch { /* noop */ }
    setUser(null);
  };

  const value = useMemo(() => ({ user, refresh, login, logout }), [user, refresh]);
  return <SupplierAuthContext.Provider value={value}>{children}</SupplierAuthContext.Provider>;
}
export const useSupplierAuth = () => useContext(SupplierAuthContext);

// ── Layout shell ────────────────────────────────────────────────────
// Per agency request, "Alternativas" and "Rechazadas" tabs are hidden
// from the supplier sidebar — those flows now live inside the per-
// booking comments thread instead of being status transitions.
const STATUS_NAV = [
  { k: 'request', label: 'Pendientes', color: '#d97706' },
  { k: 'processing', label: 'En proceso', color: '#2563eb' },
  { k: 'confirmed', label: 'Confirmadas', color: '#15803d' },
  { k: 'cancelled', label: 'Canceladas', color: '#64748b' },
];

export function SupplierLayout() {
  const { user, logout } = useSupplierAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState({});

  const refreshSummary = useCallback(async () => {
    try {
      const r = await supplierAxios.get('/api/supplier/bookings');
      setSummary(r.data.summary || {});
    } catch { /* noop on initial load */ }
  }, []);
  useEffect(() => { if (user) refreshSummary(); }, [user, refreshSummary]);

  if (user === undefined) return <div style={{ padding: 60, textAlign: 'center' }}>Cargando…</div>;
  if (!user) return <Navigate to="/supplier/login" replace />;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'grid', gridTemplateColumns: '260px 1fr' }}>
      <aside style={{ background: '#0f172a', color: '#cbd5e1', padding: '20px 14px', position: 'sticky', top: 0, height: '100vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, paddingLeft: 6 }}>
          <div style={{ width: 32, height: 32, borderRadius: 6, background: '#1e40af', display: 'grid', placeItems: 'center', fontWeight: 800, color: '#fff', fontSize: 13 }}>P</div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>PJR Suppliers</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{user.supplier?.name || ''}</div>
          </div>
        </div>

        <NavLink to="/supplier" end className={({ isActive }) => `sup-nav-item sup-nav-dash${isActive ? ' active' : ''}`} data-testid="sup-nav-dashboard">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, verticalAlign: 'middle' }}>
            <rect x="3" y="3" width="7" height="9" />
            <rect x="14" y="3" width="7" height="5" />
            <rect x="14" y="12" width="7" height="9" />
            <rect x="3" y="16" width="7" height="5" />
          </svg>
          Dashboard
        </NavLink>
        <NavLink to="/supplier/bookings" className={({ isActive }) => `sup-nav-item${isActive ? ' active' : ''}`} data-testid="sup-nav-all">
          Todas las reservas <span style={{ float: 'right', opacity: .6 }}>{summary.total || 0}</span>
        </NavLink>
        <div style={{ padding: '14px 6px 6px', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '.08em', textTransform: 'uppercase' }}>Por estado</div>
        {STATUS_NAV.map((s) => (
          <NavLink
            key={s.k}
            to={`/supplier/bookings?status=${s.k}`}
            className={({ isActive }) => `sup-nav-item${isActive ? ' active' : ''}`}
            data-testid={`sup-nav-status-${s.k}`}
          >
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: s.color, marginRight: 8 }} />
            {s.label}
            <span style={{ float: 'right', opacity: .6 }}>{summary[s.k] || 0}</span>
          </NavLink>
        ))}

        <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px solid #1e293b', fontSize: 11 }}>
          <div style={{ color: '#fff', fontSize: 13, marginBottom: 2 }}>{user.name || user.email}</div>
          <div style={{ color: '#64748b', marginBottom: 12 }}>{user.email}</div>
          <button
            onClick={async () => { await logout(); navigate('/supplier/login'); }}
            style={{ background: 'transparent', border: '1px solid #334155', color: '#cbd5e1', borderRadius: 4, padding: '6px 12px', cursor: 'pointer', width: '100%' }}
            data-testid="sup-logout"
          >
            Cerrar sesión
          </button>
        </div>
      </aside>

      <main style={{ padding: '32px 40px' }}>
        <SupplierTopBar />
        <Outlet context={{ refreshSummary, supplierAxios }} />
      </main>

      <style>{`
        .sup-nav-item { display: block; padding: 10px 14px; border-radius: 6px; color: #cbd5e1; text-decoration: none; font-size: 13px; margin-bottom: 4px; background: #1e293b; transition: background .15s, color .15s; cursor: pointer; }
        .sup-nav-item:hover { background: #334155; color: #fff; }
        .sup-nav-item.active { background: #1e40af; color: #fff; font-weight: 600; box-shadow: 0 2px 6px rgba(30,64,175,.35); }
        .sup-nav-dash { margin-bottom: 12px; }
        .sup-nav-dash.active { background: #2563eb; }
      `}</style>
    </div>
  );
}

// ── Login page ──────────────────────────────────────────────────────
export function SupplierLoginPage() {
  const { user, login } = useSupplierAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/supplier" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate('/supplier');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d === 'invalid_credentials' ? 'Email o contraseña inválidos' : (d === 'inactive_user' ? 'Usuario suspendido' : (d === 'supplier_inactive' ? 'Proveedor inactivo' : 'No se pudo iniciar sesión')));
    } finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg,#0f172a 0%, #1e3a8a 100%)', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 10, padding: '36px 32px', boxShadow: '0 20px 50px rgba(0,0,0,.25)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ width: 56, height: 56, borderRadius: 12, background: '#1e40af', display: 'grid', placeItems: 'center', margin: '0 auto 14px', color: '#fff', fontWeight: 800, fontSize: 22 }}>P</div>
          <h1 style={{ margin: 0, fontSize: 22 }}>PJR Suppliers Portal</h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 6 }}>Accedé con las credenciales que te envió PJR Travel.</p>
        </div>
        <form onSubmit={submit} data-testid="sup-login-form">
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 14, fontSize: 14 }}
            data-testid="sup-login-email"
          />
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Contraseña</label>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 20, fontSize: 14 }}
            data-testid="sup-login-password"
          />
          <button
            type="submit" disabled={busy}
            style={{ width: '100%', padding: '11px', background: '#1e40af', color: '#fff', border: 0, borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            data-testid="sup-login-submit"
          >
            {busy ? 'Ingresando…' : 'Ingresar'}
          </button>
        </form>
        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: '#64748b' }}>
          ¿Necesitas acceso? Contactá a tu coordinador en <a href="mailto:reservasonline@pjrtravel.com" style={{ color: '#1e40af' }}>reservasonline@pjrtravel.com</a>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard (counters + recent) ───────────────────────────────────
export function SupplierDashboardPage() {
  const [summary, setSummary] = useState({});
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await supplierAxios.get('/api/supplier/bookings');
        setSummary(r.data.summary || {});
        setRecent((r.data.bookings || []).slice(0, 5));
      } catch { /* anon redirect handled by layout */ }
    })();
  }, []);

  return (
    <div data-testid="sup-dashboard">
      <h1 style={{ fontSize: 26, marginBottom: 24 }}>Dashboard</h1>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12, marginBottom: 32 }}>
        {STATUS_NAV.map((s) => (
          <Link
            key={s.k}
            to={`/supplier/bookings?status=${s.k}`}
            style={{ display: 'block', padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, textDecoration: 'none', color: '#0f172a' }}
            data-testid={`sup-summary-${s.k}`}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: s.color, textTransform: 'uppercase', letterSpacing: '.06em' }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: s.color }} />
              {s.label}
            </div>
            <div style={{ fontSize: 32, fontWeight: 300, marginTop: 8 }}>{summary[s.k] || 0}</div>
          </Link>
        ))}
      </div>

      <h2 style={{ fontSize: 16, marginBottom: 12 }}>Reservas recientes</h2>
      {recent.length === 0 ? (
        <div style={{ padding: 36, textAlign: 'center', background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 8, color: '#64748b' }}>
          Aún no hay reservas asignadas.
        </div>
      ) : (
        <BookingsTable bookings={recent} />
      )}
    </div>
  );
}

// ── Bookings list ────────────────────────────────────────────────────
export function SupplierBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', q: '', travel_from: '', travel_to: '' });
  const [summary, setSummary] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  // Sync the URL `?status=…` into local state. The `searchParams`
  // dependency is critical: clicking a sidebar NavLink only mutates
  // the query string (not the path), so without this effect the
  // status filter would stay frozen on the first value the page
  // mounted with.
  useEffect(() => {
    const urlStatus = searchParams.get('status') || '';
    setFilters((f) => (f.status === urlStatus ? f : { ...f, status: urlStatus }));
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.q) params.q = filters.q;
      if (filters.travel_from) params.travel_from = filters.travel_from;
      if (filters.travel_to) params.travel_to = filters.travel_to;
      const r = await supplierAxios.get('/api/supplier/bookings', { params });
      setBookings(r.data.bookings || []);
      setSummary(r.data.summary || {});
    } catch { /* */ }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  // When the user changes the dropdown filter we also push it to the
  // URL so the sidebar highlight stays in sync and the link is
  // shareable. Empty status drops the query param entirely.
  const setStatus = (s) => {
    const next = new URLSearchParams(searchParams);
    if (s) next.set('status', s); else next.delete('status');
    setSearchParams(next, { replace: true });
  };

  return (
    <div data-testid="sup-bookings-page">
      <h1 style={{ fontSize: 26, marginBottom: 6 }}>Reservas</h1>
      <p style={{ color: '#64748b', marginBottom: 24, fontSize: 13 }}>Total visible: {bookings.length} · Total inbox: {summary.total || 0}</p>

      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 160px 160px', gap: 8, marginBottom: 18 }}>
        <select value={filters.status} onChange={(e) => setStatus(e.target.value)} style={inputStyle} data-testid="sup-filter-status">
          <option value="">Todos los estados</option>
          {STATUS_NAV.map((s) => <option key={s.k} value={s.k}>{s.label} · {summary[s.k] || 0}</option>)}
        </select>
        <input
          placeholder="Buscar booking ID, producto, pasajero, destino…"
          value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          style={inputStyle} data-testid="sup-filter-q"
        />
        <input type="date" value={filters.travel_from} onChange={(e) => setFilters({ ...filters, travel_from: e.target.value })} style={inputStyle} placeholder="Desde" />
        <input type="date" value={filters.travel_to} onChange={(e) => setFilters({ ...filters, travel_to: e.target.value })} style={inputStyle} placeholder="Hasta" />
      </div>

      {loading ? (
        <div style={{ padding: 36, textAlign: 'center', color: '#64748b' }}>Cargando…</div>
      ) : bookings.length === 0 ? (
        <div style={{ padding: 36, textAlign: 'center', background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 8, color: '#64748b' }}>
          Sin reservas que coincidan con los filtros.
        </div>
      ) : (
        <BookingsTable bookings={bookings} />
      )}
    </div>
  );
}

const inputStyle = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff' };

function statusBadge(s) {
  const def = STATUS_NAV.find((x) => x.k === s) || { color: '#64748b', label: s };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: def.color + '22', color: def.color }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: def.color }} /> {def.label}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════
// Top bar with notifications bell — sits above every supplier page so
// the bell is always one click away. Polls every 60s for fresh events.
// ════════════════════════════════════════════════════════════════════
function SupplierTopBar() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await supplierAxios.get('/api/supplier/notifications', { params: { limit: 25 } });
      setItems(r.data.notifications || []);
      setUnread(r.data.unread_count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  // Close the dropdown on outside click so it doesn't linger when the
  // user moves on with the rest of the page.
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (!e.target.closest?.('[data-bell-root]')) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const onClickItem = async (n) => {
    if (!n.read) {
      try { await supplierAxios.post(`/api/supplier/notifications/${n.notification_id}/read`); } catch { /* */ }
    }
    setOpen(false);
    if (n.booking_request_id) navigate(`/supplier/bookings/${n.booking_request_id}`);
    load();
  };

  const markAll = async () => {
    try {
      await supplierAxios.post('/api/supplier/notifications/read-all');
      load();
    } catch { /* */ }
  };

  return (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16, gap: 12 }} data-bell-root>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="sup-bell-button"
        aria-label="Notificaciones"
        style={{ position: 'relative', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 999, width: 38, height: 38, cursor: 'pointer', display: 'grid', placeItems: 'center' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
        {unread > 0 && (
          <span data-testid="sup-bell-badge" style={{ position: 'absolute', top: -3, right: -3, background: '#dc2626', color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 800, minWidth: 18, height: 18, padding: '0 5px', display: 'grid', placeItems: 'center' }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="sup-bell-dropdown" style={{ position: 'absolute', right: 0, top: 48, width: 380, maxHeight: 480, overflowY: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 12px 40px rgba(15,23,42,.18)', zIndex: 200 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff' }}>
            <strong style={{ fontSize: 13 }}>Notificaciones</strong>
            {unread > 0 && (
              <button onClick={markAll} data-testid="sup-bell-mark-all" style={{ background: 'transparent', border: 0, color: '#1e40af', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Marcar todas como leídas</button>
            )}
          </div>
          {items.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Sin notificaciones todavía.</div>
          ) : (
            items.map((n) => (
              <button
                key={n.notification_id}
                type="button"
                onClick={() => onClickItem(n)}
                data-testid={`sup-bell-item-${n.notification_id}`}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #f1f5f9', background: n.read ? '#fff' : '#eff6ff', cursor: 'pointer', border: 0, borderLeft: n.read ? 'none' : '3px solid #1e40af' }}
              >
                <div style={{ fontSize: 13, fontWeight: n.read ? 500 : 700, color: '#0f172a', marginBottom: 2 }}>{n.subject}</div>
                <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.body_text}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{new Date(n.created_at).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function BookingsTable({ bookings }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
            <th style={th}>Booking</th>
            <th style={th}>Producto</th>
            <th style={th}>Fecha viaje</th>
            <th style={th}>Pax</th>
            <th style={th}>Estado</th>
            <th style={th}>Deadline</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((b) => {
            const fp = b.request_payload || {};
            return (
              <tr key={b.request_id} style={{ borderTop: '1px solid #e2e8f0' }} data-testid={`sup-booking-row-${b.request_id}`}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{b.booking_id}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{fp.service_type}</div>
                </td>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{fp.product_name || '—'}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{fp.destinations || ''}</div>
                </td>
                <td style={td}>{fp.travel_date || '—'}</td>
                <td style={td}>{(fp.passengers || []).length}</td>
                <td style={td}>{statusBadge(b.status)}</td>
                <td style={td} data-testid={`sup-booking-deadline-${b.request_id}`}>{b.deadline_to_confirm || '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <Link to={`/supplier/bookings/${b.request_id}`} style={{ color: '#1e40af', fontWeight: 600, textDecoration: 'none' }} data-testid={`sup-booking-open-${b.request_id}`}>
                    Abrir →
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
const th = { padding: '10px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' };
const td = { padding: '12px 14px', verticalAlign: 'top' };
